import Modal, { Sheets, ModalBody, ModalHeader } from '@ingka/modal';
import Text from '@ingka/text';
import Choice, { ChoiceItem } from '@ingka/choice';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import { useNavigate } from 'react-router-dom';
import {
  useGetPermissionsQuery,
  useGetRetailUnitQuery,
} from '@lotta/self-service/lottery-fe/data-access-state';
import i18next from 'i18next';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../ProfileSlider.module.scss';

export type RetailUnitSliderProps = {
  modalVisible: boolean;
  handleCloseBtn: (event: MouseEvent<HTMLButtonElement>) => void;
  handleBackBtn: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type RetailUnitChoicesProps = {
  onRetailUnitChange: (retailunit: string | null) => void;
};

const RetailUnitChoices: React.FC<RetailUnitChoicesProps> = (props) => {
  const { onRetailUnitChange } = props;

  const { data, isLoading, error } = useGetPermissionsQuery({});

  const { t } = useTranslation();

  if (isLoading || error || !data) {
    return (
      <div className={styles['LocalizationDropdown']}>
        <Choice>
          <ChoiceItem disabled id="disabledChoice">
            {t('header-modal-retail-unit-change')}
          </ChoiceItem>
        </Choice>
      </div>
    );
  }
  const retailUnits = data.permissions.map((permission) => {
    return permission.retailUnitId;
  });

  return (
    <Choice
      size="medium"
      data-testid="retailUnitSelect"
      onSelect={(retailUnit: string | null) => {
        onRetailUnitChange(retailUnit);
      }}
    >
      {retailUnits.map((retailUnit) => {
        return <ChoiceItem id={retailUnit} key={retailUnit} title={retailUnit} />;
      })}
    </Choice>
  );
};

export const RetailUnitSlider: React.FC<RetailUnitSliderProps> = (props) => {
  const { modalVisible, handleCloseBtn, handleBackBtn } = props;
  const { retailUnit } = useLocale();
  const [retailUnitId, setRetailUnitId] = useState<string>(retailUnit);
  const navigate = useNavigate();
  const { currentData: retailUnitData, isFetching } = useGetRetailUnitQuery({
    retailUnit: retailUnitId,
  });

  useEffect(() => {
    if (retailUnit !== retailUnitId && retailUnitData && !isFetching) {
      const defaultLocale = `${retailUnitData.retailUnit.selfService.defaultLanguage.toLowerCase()}-${retailUnitId}`;
      i18next.changeLanguage(defaultLocale);

      const newPath = `/${retailUnitId.toLocaleLowerCase()}/${
        retailUnitData.retailUnit.selfService.defaultLanguage
      }/`;
      navigate(newPath);
    }
  }, [retailUnit, retailUnitId, retailUnitData, isFetching, navigate]);

  const { t } = useTranslation();

  const handleRetailUnitChange = (retailUnit: string | null) => {
    setRetailUnitId(retailUnit ?? retailUnitId);
  };

  return (
    <Modal visible={modalVisible} handleCloseBtn={handleCloseBtn}>
      <Sheets
        footer={null}
        header={
          <ModalHeader
            backBtnClick={handleBackBtn}
            title={`${t('header-modal-retail-unit-change')}`}
          />
        }
      >
        <ModalBody>
          <Text className={styles['LocalizationDropdown']}>
            {t('header-modal-retail-unit-select')}
          </Text>
          <RetailUnitChoices onRetailUnitChange={handleRetailUnitChange} />
        </ModalBody>
      </Sheets>
    </Modal>
  );
};
